<template>
  <div class="rf">
    <UiSelect :model-value="preset" :options="presetOptions" @update:model-value="choosePreset" />
    <div v-if="modelValue && preset === 'custom'" class="rf__custom">
      <div class="rf__row">
        <span>{{ t('calendar.recurrence.every') }}</span>
        <UiInput class="rf__num" type="number" :model-value="modelValue.interval ?? 1" min="1" max="99" @update:model-value="set({ interval: Number($event) || 1 })" />
        <UiSelect class="rf__freq" :model-value="modelValue.freq" :options="freqOptions" @update:model-value="set({ freq: $event })" />
      </div>
      <div v-if="modelValue.freq === 'weekly'" class="rf__days" role="group" :aria-label="t('calendar.recurrence.days')">
        <button
          v-for="day in weekOrder"
          :key="day"
          type="button"
          class="rf__day"
          :class="{ 'rf__day--on': (modelValue.byWeekday ?? []).includes(day) }"
          :aria-pressed="(modelValue.byWeekday ?? []).includes(day)"
          @click="toggleDay(day)"
        >{{ dayName(day) }}</button>
      </div>
      <UiSelect
        v-if="modelValue.freq === 'monthly'"
        :model-value="modelValue.monthlyBy ?? 'day'"
        :options="monthlyOptions"
        @update:model-value="set({ monthlyBy: $event })"
      />
    </div>
    <div v-if="modelValue" class="rf__row rf__end">
      <UiSelect class="rf__endkind" :model-value="endKind" :options="endOptions" @update:model-value="chooseEnd" />
      <UiInput v-if="endKind === 'until'" type="date" class="rf__until" :model-value="modelValue.until ?? ''" :min="startDate" @update:model-value="set({ until: $event || null, count: null })" />
      <template v-if="endKind === 'count'">
        <UiInput type="number" class="rf__num" min="1" max="730" :model-value="modelValue.count ?? 10" @update:model-value="set({ count: Number($event) || 1, until: null })" />
        <span>{{ t('calendar.recurrence.times') }}</span>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { UiInput, UiSelect } from '@general/ui-kit'

import { weekdayOf, parseDate } from '../lib/time.mjs'

/**
 * Repetition in two steps: the usual choices as one tap (daily, every week on
 * this day, every month, every weekday), and «custom» for the rest — every N
 * days/weeks/months, which weekdays, until a date or N times.
 */
const props = defineProps({
  modelValue: { type: Object, default: null },
  startDate: { type: String, required: true },
})
const emit = defineEmits(['update:modelValue'])
const { t, locale } = useI18n()

const weekOrder = [1, 2, 3, 4, 5, 6, 0]
const startWeekday = computed(() => weekdayOf(props.startDate))
const dayName = day => new Intl.DateTimeFormat(locale.value, { weekday: 'short', timeZone: 'UTC' })
  .format(new Date(Date.UTC(2024, 0, 7 + day, 12))).slice(0, 3)
const longDay = day => new Intl.DateTimeFormat(locale.value, { weekday: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, 7 + day, 12)))

const preset = computed(() => {
  const rule = props.modelValue
  if (!rule) return 'none'
  const days = [...(rule.byWeekday ?? [])].sort().join(',')
  if ((rule.interval ?? 1) === 1) {
    if (rule.freq === 'daily') return 'daily'
    if (rule.freq === 'weekly' && days === '1,2,3,4,5') return 'weekdays'
    if (rule.freq === 'weekly' && (!days || days === String(startWeekday.value))) return 'weekly'
    if (rule.freq === 'monthly' && (rule.monthlyBy ?? 'day') === 'day') return 'monthly'
    if (rule.freq === 'yearly') return 'yearly'
  }
  return 'custom'
})
const presetOptions = computed(() => [
  { value: 'none', label: t('calendar.recurrence.none') },
  { value: 'daily', label: t('calendar.recurrence.daily') },
  { value: 'weekdays', label: t('calendar.recurrence.weekdays') },
  { value: 'weekly', label: t('calendar.recurrence.weekly', { day: longDay(startWeekday.value) }) },
  { value: 'monthly', label: t('calendar.recurrence.monthly', { day: parseDate(props.startDate).day }) },
  { value: 'yearly', label: t('calendar.recurrence.yearly') },
  { value: 'custom', label: t('calendar.recurrence.custom') },
])
const freqOptions = computed(() => ['daily', 'weekly', 'monthly', 'yearly']
  .map(freq => ({ value: freq, label: t(`calendar.recurrence.units.${freq}`) })))
const monthlyOptions = computed(() => [
  { value: 'day', label: t('calendar.recurrence.monthlyDay', { day: parseDate(props.startDate).day }) },
  { value: 'weekday', label: t('calendar.recurrence.monthlyWeekday', { nth: Math.ceil(parseDate(props.startDate).day / 7), day: longDay(startWeekday.value) }) },
])
const endKind = computed(() => (props.modelValue?.until ? 'until' : props.modelValue?.count ? 'count' : 'never'))
const endOptions = computed(() => [
  { value: 'never', label: t('calendar.recurrence.never') },
  { value: 'until', label: t('calendar.recurrence.until') },
  { value: 'count', label: t('calendar.recurrence.count') },
])

function choosePreset(value) {
  const keep = { until: props.modelValue?.until ?? null, count: props.modelValue?.count ?? null }
  const rules = {
    none: null,
    daily: { freq: 'daily', interval: 1, ...keep },
    weekdays: { freq: 'weekly', interval: 1, byWeekday: [1, 2, 3, 4, 5], ...keep },
    weekly: { freq: 'weekly', interval: 1, byWeekday: [startWeekday.value], ...keep },
    monthly: { freq: 'monthly', interval: 1, monthlyBy: 'day', ...keep },
    yearly: { freq: 'yearly', interval: 1, ...keep },
    custom: { freq: 'weekly', interval: 2, byWeekday: [startWeekday.value], ...keep },
  }
  emit('update:modelValue', rules[value] ?? null)
}
function set(patch) {
  const next = { ...props.modelValue, ...patch }
  if (patch.freq === 'weekly' && !next.byWeekday?.length) next.byWeekday = [startWeekday.value]
  emit('update:modelValue', next)
}
function toggleDay(day) {
  const days = new Set(props.modelValue.byWeekday ?? [])
  if (days.has(day) && days.size > 1) days.delete(day)
  else days.add(day)
  set({ byWeekday: [...days] })
}
function chooseEnd(kind) {
  if (kind === 'never') set({ until: null, count: null })
  if (kind === 'until') set({ until: props.startDate, count: null })
  if (kind === 'count') set({ count: 10, until: null })
}
</script>

<style scoped>
.rf { display: flex; flex-direction: column; gap: 8px; }
.rf__custom { display: flex; flex-direction: column; gap: 8px; padding: 10px; border: 1px solid var(--color-border); border-radius: var(--radius); background: var(--color-surface-2); }
.rf__row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: var(--text-sm); }
.rf__num { width: 84px; }
.rf__freq, .rf__endkind { min-width: 150px; }
.rf__until { width: 170px; }
.rf__days { display: flex; gap: 6px; flex-wrap: wrap; }
.rf__day { width: 42px; height: 42px; border-radius: 50%; border: 1px solid var(--color-border); background: var(--color-surface); font-size: var(--text-xs); text-transform: capitalize; cursor: pointer; color: var(--color-text); }
.rf__day--on { background: var(--color-primary); border-color: var(--color-primary); color: var(--color-text-on-primary); }
</style>
